import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogTitle,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { AlertDialogTrigger } from '@codes/ui-kit/components/ui/alert-dialog';
import { Card } from '@codes/ui-kit/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { UserView } from '@lemoncloud/codes-backend-api';
import { createAsyncDelay } from '@lemoncloud/lemon-web-core';
import { ToastTitle } from '@radix-ui/react-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, LogOut, Mail, User } from 'lucide-react';

import { useLoader } from '@codes/loader';
import { ownerQueryKeys, useRefuseMember, useUpdateMemberRole } from '@codes/owner';
import { Badge } from '@codes/ui-kit';
import { useWebCoreStore } from '@codes/web-core';

import { useWorkspaceService } from '../../home/hooks';

export const MemberCard = ({ member }: { member: UserView }) => {
    const { i18n } = useTranslation();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const { setIsLoading } = useLoader();
    const userId = useWebCoreStore(state => state.userId);
    const queryClient = useQueryClient();

    const updateMemberRole = useUpdateMemberRole();
    const refuseMember = useRefuseMember();

    const { isOwner: isWorkspaceOwner } = useWorkspaceService();

    const memberRole = member.role || member.$role?.role;
    const isOwner = memberRole === 'owner';

    const getRoleDisplay = (member: UserView) => {
        const role = member.role || member.$role?.role;
        const roleMap: Record<string, string> = {
            guest: 'View',
            member: 'Edit',
        };
        return roleMap[role as string] || '';
    };

    const handleRemoveMember = async () => {
        setIsLoading(true);
        try {
            await refuseMember.mutateAsync(member.id);
            await createAsyncDelay(2000);
            await queryClient.invalidateQueries(ownerQueryKeys.invalidateList());
            toast({
                title: <ToastTitle>{t('project.removeMemberToast')}</ToastTitle>,
            });
        } catch (error) {
            console.error('Failed to remove member:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRoleUpdate = async (newRole: 'guest' | 'member') => {
        if (memberRole === newRole) {
            setOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            await updateMemberRole.mutateAsync({
                id: member.id,
                role: newRole,
            });
            await createAsyncDelay(2000);

            await queryClient.invalidateQueries(ownerQueryKeys.invalidateList());

            setOpen(false);
        } catch (error) {
            console.error('Failed to update role:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const isVerified = () => {
        return member.$role?.invite$?.verifiedAt || userId === member.id;
    };

    return (
        <Card
            className={`${
                isVerified() ? '' : 'bg-card'
            } px-[18px] py-[22px] rounded-[18px] flex flex-col justify-between gap-7`}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div
                        className={`w-9 h-9 flex items-center justify-center rounded-full ${
                            isVerified() ? 'bg-primary' : 'bg-avatar border border-dashed border-[#BABCC0]'
                        }`}
                    >
                        <User className={`w-6 h-6 ${isVerified() ? 'text-white' : 'text-disabled-text'}`} />
                    </div>
                    <span className={`${isVerified() ? '' : 'text-disabled-text'} text-[18px] font-medium`}>
                        {member.nick || member.name} {userId === member.id && `(you)`}
                    </span>
                </div>
                {!isOwner && isWorkspaceOwner && (
                    <AlertDialog>
                        <AlertDialogTrigger>
                            <LogOut className="w-5 h-5 text-sub-title" />
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogTitle>{t('project.removeMemberConfirmation')}</AlertDialogTitle>
                            <AlertDialogFooter>
                                <AlertDialogCancel>{t('project.cancel')}</AlertDialogCancel>
                                <AlertDialogAction onClick={handleRemoveMember}>
                                    {t('project.removeMember')}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                )}
            </div>
            <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-[10px]">
                    <Badge variant="B" className="h-[22px] bg-hover text-xs text-text gap-[6px] px-2">
                        <Mail className="w-4 h-4" />
                        E-MAIL
                    </Badge>
                    <div className="text-right break-all">{member.email || member.$user?.email}</div>
                </div>
                <div className="flex items-center justify-between gap-2">
                    <Badge variant="B" className="h-[22px] bg-hover text-xs text-text gap-[6px] px-2">
                        ROLE
                    </Badge>
                    {isOwner ? (
                        <div className="text-[#004D8D] text-base font-medium">Owner</div>
                    ) : (
                        <DropdownMenu open={open} onOpenChange={setOpen}>
                            <DropdownMenuTrigger
                                className="focus-within:outline-0 flex items-center gap-1"
                                disabled={!isWorkspaceOwner}
                            >
                                <span>{getRoleDisplay(member)}</span>
                                {isWorkspaceOwner && (
                                    <ChevronDown className={open ? 'w-4 h-4 rotate-180' : 'w-4 h-4'} />
                                )}
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-[144px] flex flex-col gap-[2px] text-base">
                                <DropdownMenuItem
                                    className="hover:!text-point flex items-center justify-between"
                                    onClick={() => handleRoleUpdate('guest')}
                                >
                                    <span>View</span>
                                    {memberRole === 'guest' && <Check className="h-4 w-4 text-point" />}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    className="hover:!text-point flex items-center justify-between"
                                    onClick={() => handleRoleUpdate('member')}
                                >
                                    <span>Edit</span>
                                    {memberRole === 'member' && <Check className="h-4 w-4 text-point" />}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>
        </Card>
    );
};
